// @flow

// Requests

import {
  getPaymentAccountsRequest,
  getPaymentAccountRequest,
  addPaymentAccountRequest,
  editPaymentAccountRequest,
  deletePaymentAccountRequest,
  getPaymentCardFrameUrlRequest,
} from '../../backend/walletRequests';
import { replace } from 'connected-react-router';

// Helpers
import { to, getPaymentRoute, serializeParams } from '../../lib/helpers';
import { gaActions } from '../gaActionTypes';
import {
  submitPayment,
  submitDLRPayment,
  submitPaymentTransact,
} from '../../backend/paymentRequests';
import {
  checkRequestFailure,
  handleRequestFailure,
} from '../../lib/validation';
import { bodyHasError, walletErrors } from '../../lib/validation/serverErrors';

// Types
import { type PaymentContextObject } from '../../components/Payment/Payment';

//Selectors
import { selectPathname } from '../route/routeSelectors';
import { selectAppBasicInfo } from '../app/appSelectors';
import {
  selectDonationOptions,
  selectDonationValues,
  selectOrganDonorOption,
  selectSurrenderCardOption,
  selectRenewAddressChangeFlag,
  selectRenewalHomeAddress,
  selectRenewalMailingAddress,
  selectMailingSameAsHomeAddress,
  selectEmergencyContacts,
  selectEmergencyContactsFailedStatus,
  selectUpgradeFlag,
  selectJointRenewalUpgradeFlag,
  selectSurrenderDLFlag,
  selectTotalFeesAndDonations,
  selectVoterRegistration,
  selectVolunteerElectionJudge,
  selectVoterRegistrationAppType,
} from '../agency/dlr/dlrSelectors';
import { selectCountyCode } from '../agency/dmv/dmvSelectors';
import { selectAppSlugByPath } from '../agency/agencySelectors';

// Actions
import { storePaymentSuccess } from '../agency/dlr/dlrActions';
import { formattDMVPayment } from '../agency/dmv/dmvActions';
import { openModal } from '../../reducers/modal/modalActions';
import { formatTPWDPayment } from '../agency/tpwd/tpwdActions';
import { APP_TRANSACTION_IDS } from '../../reducers/app/appActions';
import { formatTXHHSPayment } from '../agency/txhhs/txhhsActions';
import { formatTXRCPayment } from '../agency/txrc/txrcActions';

// Agencies config
import agencies from '../../agency-config/agencies';

// Helper function to create fee calculation log
const createFeeCalculationLog = state => {
  const basicInfo = selectAppBasicInfo(state);
  const donationValue = selectDonationValues(state);
  const renewAddressChangeFlag = selectRenewAddressChangeFlag(state);
  const upgradeFlag = selectUpgradeFlag(state);
  const jointRenewalUpgradeFlag = selectJointRenewalUpgradeFlag(state);
  const surrenderDLFlag = selectSurrenderDLFlag(state);

  // Create fee calculation log
  const duplicateFeeValue = renewAddressChangeFlag
    ? basicInfo.duplicateFee
    : undefined;
  const renewalFeeValue =
    !renewAddressChangeFlag && upgradeFlag && !jointRenewalUpgradeFlag
      ? basicInfo.upgradeFee
      : !renewAddressChangeFlag && !jointRenewalUpgradeFlag && surrenderDLFlag
      ? basicInfo.dlSurrenderForIdFee
      : !renewAddressChangeFlag
      ? basicInfo.renewalFee
      : undefined;

  const transactionId = jointRenewalUpgradeFlag
    ? APP_TRANSACTION_IDS.SUBMIT_UPGRADE_RENEWAL_PAYMENT
    : upgradeFlag
    ? APP_TRANSACTION_IDS.SUBMIT_UPGRADE_PAYMENT
    : surrenderDLFlag
    ? APP_TRANSACTION_IDS.SUBMIT_SURRENDER_PAYMENT
    : renewAddressChangeFlag
    ? APP_TRANSACTION_IDS.REPLACEMENT
    : APP_TRANSACTION_IDS.SUBMIT_RENEWAL_PAYMENT;

  // Get existing log and append to it
  // Added to capture fee calculation data and send to backend to analyze fee discrepancies.
  // Will be printed under header - Frontend log for payment analysis
  const existingTotalFeesAndDonationsLog = selectTotalFeesAndDonations(state);
  let totalFeeAndDonationLog = existingTotalFeesAndDonationsLog
    ? `${existingTotalFeesAndDonationsLog}\n--Start SubmitDLRLRRenewal--`
    : `--Start SubmitDLRLRRenewal--`;
  totalFeeAndDonationLog += ` renewAddressChangeFlag: ${renewAddressChangeFlag}`;
  totalFeeAndDonationLog += `, upgradeFlag: ${upgradeFlag}`;
  totalFeeAndDonationLog += `, jointRenewalUpgradeFlag: ${jointRenewalUpgradeFlag}`;
  totalFeeAndDonationLog += `, surrenderDLFlag: ${surrenderDLFlag}`;
  totalFeeAndDonationLog += `, duplicateFee: ${duplicateFeeValue ||
    'undefined'}`;
  totalFeeAndDonationLog += `, renewalFee: ${renewalFeeValue || 'undefined'}`;
  totalFeeAndDonationLog += `, serviceFee: ${basicInfo.serviceFee}`;
  totalFeeAndDonationLog += `, bestDonation: ${donationValue?.bestDonationOption}`;
  totalFeeAndDonationLog += `, glendaDonationOption: ${donationValue?.glendaDonationOption}`;
  totalFeeAndDonationLog += `, fvaDonationOption: ${donationValue?.fvaDonationOption}`;
  totalFeeAndDonationLog += `, evidenceTestingDonationOption: ${donationValue?.evidenceTestingDonationOption}`;
  totalFeeAndDonationLog += `, youthFundDonationOption: ${donationValue?.youthFundDonationOption}`;
  totalFeeAndDonationLog += `, supportAdoptionDonationOption: ${donationValue?.supportAdoptionDonationOption}`;
  totalFeeAndDonationLog += `, totalAmount: ${donationValue.total}`;
  totalFeeAndDonationLog += `, transactionId: ${transactionId}`;
  totalFeeAndDonationLog += `--End SubmitDLRLRRenewal--`;

  return totalFeeAndDonationLog;
};

export const GET_PAYMENT_DATA = '[PAYMENT] Get Payment Data';
export const STORE_PAYMENT_DATA = '[PAYMENT] Store Payment Data';
export const CLEAR_PAYMENT_DATA = '[PAYMENT] Clear Payment Data';
export const CLEAR_ERROR_DATA = '[PAYMENT] Clear Payment Error Data';
export const PAYMENT_SUBMITTED = `[PAYMENT] ${gaActions.submit} Submitted`;

const ACCOUNT_SETTINGS_ERRORS = ['MGMW_EX_4000', 'MGMW_EX_4001'];

// Build dynamic AGENCY_IDS from agencies config
export const AGENCY_IDS = (() => {
  const map = {};
  const agenciesArr = Array.isArray(agencies) ? agencies : Object.values(agencies);
  agenciesArr.forEach(({ agency }) => {
    if (agency && agency.agencyShortName && agency.agencyId) {
      map[agency.agencyShortName] = agency.agencyId;
    }
  });
  return map;
})();

// Build dynamic appSlugMap from agencies config
const appSlugMap = (() => {
  const map = {};
  const agenciesArr = Array.isArray(agencies) ? agencies : Object.values(agencies);
  agenciesArr.forEach(({ agency }) => {
    if (agency && agency.applicationCodes && agency.agencyShortName) {
      const shortName = agency.agencyShortName.toLowerCase();
      Object.entries(agency.applicationCodes).forEach(([appCode, appSlug]) => {
        // Map each app slug to agency short name
        map[appSlug] = shortName;
      });
    }
  });
  // Special case: dmvrr should stay as 'dmvrr' instead of 'dmv' for /getAllPaymentCards and getPaymentAccountForm api call
  if (map['dmvrr']) {
    map['dmvrr'] = 'dmvrr';
  }
  
  return map;
})();

export const handlePaymentResponse = (
  {
    body,
    response,
    err,
    formikActions,
  }: { body: any, response: any, err: any, formikActions?: any },
  onSuccess,
  onError
) => (dispatch, getState) => {
  dispatch({ type: PAYMENT_SUBMITTED, payload: body });

  if (err || checkRequestFailure({ response, body })) {
    const pathName = selectPathname(getState());

    let serverErrors;
    if (formikActions) {
      serverErrors = handleRequestFailure(body, formikActions);
    }

    const paymentErrorCodes = [
      'MGMW_EX_3108',
      'MGMW_EX_3508',
      'MGMW_EX_0000',
      'MGMW_EX_3111',
      'MGMW_EX_3112',
      'MGMW_EX_3905',
      'MGMW_EX_3906',
      'MGMW_EX_3907',
    ];

    if (
      bodyHasError(body, { errorCodes: paymentErrorCodes }) ||
      body.status !== 'SUCCESS'
    ) {
      if (pathName.includes('payment')) {
        dispatch(getPaymentAccounts());
      }

      if (
        bodyHasError(body, {
          errorCodes: ['MGMW_EX_3111', 'MGMW_EX_3508'],
        })
      ) {
        return Promise.reject({ body, response, serverErrors });
      }

      if (
        bodyHasError(body, {
          errorCodes: ['MGMW_EX_3905', 'MGMW_EX_3906', 'MGMW_EX_3907'],
        })
      ) {
        const errorCode = body.errors.ERROR;
        dispatch(openModal('TPWD_Transaction_Submitted_Modal', { errorCode }));
        return Promise.reject({ body, response });
      }

      if (
        bodyHasError(body, {
          errorCodes: ['MGMW_EX_3112'],
        })
      ) {
        return Promise.reject({ body, response, serverErrors });
      }
    }

    if (bodyHasError(body, { errorCodes: ['MGMW_E_3102'] })) {
      dispatch(replace(getPaymentRoute('/error', pathName)));
      return Promise.reject({ body, response, serverErrors });
    }

    if (typeof onError === 'function') {
      onError(body);
    }

    return Promise.reject({ body, response, serverErrors });
  }

  if (typeof onSuccess === 'function') {
    onSuccess(body);
  }

  return Promise.resolve({ body, response });
};

export const getPaymentAccounts = () => async (dispatch, getState) => {
  const appSlug = selectAppSlugByPath(getState());
  // Special case where we need to pass 'dlr' instead of 'dps' only for the /getAllPaymentCards api call
  const app = appSlug === 'dpslr' ? 'dlr' : appSlugMap[appSlug];

  try {
    const {
      body: { status, data },
    } = await dispatch(getPaymentAccountsRequest(app));

    const paymentAccounts = data.map(({ cardPaymentInfo = {} }) => {
      const { cvvRequired } = cardPaymentInfo;
      return {
        ...cardPaymentInfo,
        cvvRequired: cvvRequired == null || cvvRequired,
      };
    });

    const payload = {
      walletStatus: status,
      paymentAccounts,
    };

    dispatch(storePaymentData(payload));
    return Promise.resolve(paymentAccounts);
  } catch (err) {
    if (bodyHasError(err.body, { containsExactError: walletErrors })) {
      dispatch(
        storePaymentData({ walletStatus: 'EXCEPTION', paymentAccounts: [] })
      );
      return Promise.resolve([]);
    } else {
      Promise.reject(err);
    }
  }
};

export const getPaymentAccount = (paymentAccountId: string) => async (
  dispatch: Function
) => {
  try {
    const { body } = await dispatch(
      getPaymentAccountRequest({ paymentAccountId })
    );

    return Promise.resolve(body);
  } catch (err) {
    if (bodyHasError(err.body, { containsExactError: walletErrors })) {
      return Promise.resolve({});
    } else {
      Promise.reject(err);
    }
  }
};

export const storePaymentData = (payload: any) => (dispatch: Function) =>
  dispatch({ type: STORE_PAYMENT_DATA, payload });

export const clearPaymentData = () => (dispatch: Function) =>
  dispatch({ type: CLEAR_PAYMENT_DATA });

export const clearErrorData = () => (dispatch: Function) =>
  dispatch({ type: CLEAR_ERROR_DATA });

export const submitPaymentFromAccountList = (
  values: { selectPayment: Object },
  formikActions: FormikActions,
  { onSuccess, onError }: PaymentContextObject,
  translate
) => async (dispatch: Function, getState: Function) => {
  if (!values.selectPayment) {
    formikActions.setStatus({
      serverErrors: { formError: { id: 'Field_PaymentMethodRequired' } },
    });
    formikActions.setSubmitting(false);
    return;
  }

  const state = getState();
  const basicInfo = selectAppBasicInfo(state);
  const donationValue = selectDonationValues(state);
  const donationOption = selectDonationOptions(state);
  const organDonorOption = selectOrganDonorOption(state);
  const surrenderCardOption = selectSurrenderCardOption(state);
  const renewAddressChangeFlag = selectRenewAddressChangeFlag(state);
  const upgradeFlag = selectUpgradeFlag(state);
  const jointRenewalUpgradeFlag = selectJointRenewalUpgradeFlag(state);
  const renewalHomeAddress = selectRenewalHomeAddress(state);
  const renewalMailingAddress = selectRenewalMailingAddress(state);
  const mailingSameAsHomeAddress = selectMailingSameAsHomeAddress(state);
  const emergencyContacts = selectEmergencyContacts(state);
  const emergencyContactsFailedStatus = selectEmergencyContactsFailedStatus(
    state
  );
  const voterRegistration = selectVoterRegistration(state);
  const volunteerElectionJudge = selectVolunteerElectionJudge(state);
  const voterRegistrationAppType = selectVoterRegistrationAppType(state);
  const surrenderDLFlag = selectSurrenderDLFlag(state);
  if (basicInfo.agencyId === AGENCY_IDS.DMV) {
    const dmvReqBody = await dispatch(formattDMVPayment(values));

    const payload = {
      ...dmvReqBody,
      transactionInfo: {
        ...dmvReqBody.transactionInfo,
        cvv: values.cvv,
        paymentAccountId: values.selectPayment.paymentAccountId,
        paymentAccountType: values.selectPayment.cardType,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TPWD) {
    const tpwdReqBody = await dispatch(formatTPWDPayment(translate));

    const payload = {
      ...tpwdReqBody,
      transactionInfo: {
        ...tpwdReqBody.transactionInfo,
        cvv: values.cvv,
        paymentAccountId: values.selectPayment.paymentAccountId,
        paymentAccountType: values.selectPayment.cardType,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.DPS) {
    const selectedPayment = {
      cvv: values.cvv,
      paymentAccountId: values.selectPayment.paymentAccountId,
      paymentAccountType: values.selectPayment.cardType,
    };

    // Create fee calculation log using reusable function
    const totalFeeAndDonationLog = createFeeCalculationLog(state);

    const [{ body, response }, err] = await to(
      dispatch(
        submitDLRPayment(selectedPayment, {
          ...basicInfo,
          donationOption,
          donationValue,
          emergencyContacts,
          emergencyContactsFailedStatus,
          jointRenewalUpgradeFlag,
          mailingSameAsHomeAddress,
          organDonorOption,
          renewAddressChangeFlag,
          renewalHomeAddress,
          renewalMailingAddress,
          surrenderCardOption,
          upgradeFlag,
          surrenderDLFlag,
          totalFeeAndDonationLog,
          voterRegistration,
          volunteerElectionJudge,
          voterRegistrationAppType,
        })
      )
    );

    if (body && body.status !== 'ERROR') {
      dispatch(storePaymentSuccess(body.data));
    }

    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TXHHS) {
    const txhhsReqBody = await dispatch(formatTXHHSPayment(translate));

    const payload = {
      ...txhhsReqBody,
      transactionInfo: {
        ...txhhsReqBody.transactionInfo,
        cvv: values.cvv,
        paymentAccountId: values.selectPayment.paymentAccountId,
        paymentAccountType: values.selectPayment.cardType,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TXRC) {
    const txrcReqBody = await dispatch(formatTXRCPayment(translate));

    const payload = {
      ...txrcReqBody,
      transactionInfo: {
        ...txrcReqBody.transactionInfo,
        cvv: values.cvv,
        paymentAccountId: values.selectPayment.paymentAccountId,
        paymentAccountType: values.selectPayment.cardType,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else {
    const payload = {
      cvv: values.cvv,
      paymentAccountId: values.selectPayment.paymentAccountId,
      paymentAccountType: values.selectPayment.cardType,
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPayment(payload, basicInfo))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  }
};

export const submitPaymentFromForm = (
  { onSuccess, onError }: PaymentContextObject,
  paymentPayload: any,
  formikActions: FormikActions,
  translate
) => async (dispatch: Function, getState: Function) => {
  const state = getState();
  const basicInfo = selectAppBasicInfo(state);

  if (basicInfo.agencyId === AGENCY_IDS.DPS) {
    const donationValue = selectDonationValues(state);
    const donationOption = selectDonationOptions(state);
    const organDonorOption = selectOrganDonorOption(state);
    const surrenderCardOption = selectSurrenderCardOption(state);
    const upgradeFlag = selectUpgradeFlag(state);
    const jointRenewalUpgradeFlag = selectJointRenewalUpgradeFlag(state);
    const renewAddressChangeFlag = selectRenewAddressChangeFlag(state);
    const renewalHomeAddress = selectRenewalHomeAddress(state);
    const renewalMailingAddress = selectRenewalMailingAddress(state);
    const mailingSameAsHomeAddress = selectMailingSameAsHomeAddress(state);
    const emergencyContacts = selectEmergencyContacts(state);
    const emergencyContactsFailedStatus = selectEmergencyContactsFailedStatus(
      state
    );
    const voterRegistration = selectVoterRegistration(state);
    const volunteerElectionJudge = selectVolunteerElectionJudge(state);
    const voterRegistrationAppType = selectVoterRegistrationAppType(state);
    const paymentData = {
      address: paymentPayload.address,
      cardNumber: paymentPayload.cardNumber,
      cvv: paymentPayload.cvv,
      expirationDate: paymentPayload.expirationDate,
      lastFourDigitsCard: paymentPayload.cardNumber,
      nameOnCard: paymentPayload.nameOnCard,
      paymentAccountType: paymentPayload.paymentAccountType,
      paymentToken: paymentPayload.paymentToken,
      saveCardToWallet: paymentPayload.saveSelectedCard,
    };
    const surrenderDLFlag = selectSurrenderDLFlag(state);

    // Create fee calculation log using reusable function
    const totalFeeAndDonationLog = createFeeCalculationLog(state);

    const [{ body, response }, err] = await to(
      dispatch(
        submitDLRPayment(paymentData, {
          ...basicInfo,
          donationOption,
          donationValue,
          emergencyContacts,
          emergencyContactsFailedStatus,
          jointRenewalUpgradeFlag,
          mailingSameAsHomeAddress,
          organDonorOption,
          renewAddressChangeFlag,
          renewalHomeAddress,
          renewalMailingAddress,
          surrenderCardOption,
          upgradeFlag,
          surrenderDLFlag,
          totalFeeAndDonationLog,
          voterRegistration,
          volunteerElectionJudge,
          voterRegistrationAppType
        })
      )
    );

    if (body && body.status !== 'ERROR') {
      dispatch(storePaymentSuccess(body.data));
    }

    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TPWD) {
    const paymentData = {
      billingAddress: paymentPayload.address,
      cardExpiration: paymentPayload.expirationDate,
      cardType: paymentPayload.paymentAccountType,
      cvv: paymentPayload.cvv,
      nameOnCard: paymentPayload.nameOnCard,
      lastFourDigitsCard: paymentPayload.cardNumber,
      paymentAccountType: paymentPayload.paymentAccountType,
      paymentToken: paymentPayload.paymentToken,
      saveCardToWallet: paymentPayload.saveSelectedCard,
    };

    const tpwdReqBody = await dispatch(formatTPWDPayment(translate));

    const payload = {
      ...tpwdReqBody,
      transactionInfo: {
        ...tpwdReqBody.transactionInfo,
        ...paymentData,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );

    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.DMV) {
    const dmvReqBody = await dispatch(formattDMVPayment());

    const payload = {
      ...dmvReqBody,
      transactionInfo: {
        ...dmvReqBody.transactionInfo,
        billingAddress: paymentPayload.address,
        cardExpiration: paymentPayload.expirationDate,
        cvv: paymentPayload.cvv,
        lastFourDigitsCard: paymentPayload.cardNumber,
        nameOnCard: paymentPayload.nameOnCard,
        paymentAccountType: paymentPayload.paymentAccountType,
        paymentToken: paymentPayload.paymentToken,
        saveCardToWallet: paymentPayload.saveSelectedCard,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TXHHS) {
    const paymentData = {
      billingAddress: paymentPayload.address,
      cardExpiration: paymentPayload.expirationDate,
      cardType: paymentPayload.paymentAccountType,
      cvv: paymentPayload.cvv,
      nameOnCard: paymentPayload.nameOnCard,
      lastFourDigitsCard: paymentPayload.cardNumber,
      paymentAccountType: paymentPayload.paymentAccountType,
      paymentToken: paymentPayload.paymentToken,
      saveCardToWallet: paymentPayload.saveSelectedCard,
    };

    const txhhsReqBody = await dispatch(formatTXHHSPayment(translate));

    const payload = {
      ...txhhsReqBody,
      transactionInfo: {
        ...txhhsReqBody.transactionInfo,
        ...paymentData,
      },
    };

    const [{ body, response }, err] = await to(
      dispatch(submitPaymentTransact(payload))
    );

    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  } else if (basicInfo.agencyId === AGENCY_IDS.TXRC) {
  const paymentData = {
    billingAddress: paymentPayload.address,
    cardExpiration: paymentPayload.expirationDate,
    cardType: paymentPayload.paymentAccountType,
    cvv: paymentPayload.cvv,
    nameOnCard: paymentPayload.nameOnCard,
    lastFourDigitsCard: paymentPayload.cardNumber,
    paymentAccountType: paymentPayload.paymentAccountType,
    paymentToken: paymentPayload.paymentToken,
    saveCardToWallet: paymentPayload.saveSelectedCard,
  };

  const txrcReqBody = await dispatch(formatTXRCPayment(translate));

  const payload = {
    ...txrcReqBody,
    transactionInfo: {
      ...txrcReqBody.transactionInfo,
      ...paymentData,
    },
  };

  const [{ body, response }, err] = await to(
    dispatch(submitPaymentTransact(payload))
  );

  return dispatch(
    handlePaymentResponse(
      { body, response, err, formikActions },
      onSuccess,
      onError
    )
  );
} else {
    const { saveSelectedCard: saveCardToWallet, ...data } = paymentPayload;
    const paymentData = {
      saveCardToWallet,
      ...data,
    };
    const [{ body, response }, err] = await to(
      dispatch(submitPayment(paymentData, basicInfo))
    );
    return dispatch(
      handlePaymentResponse(
        { body, response, err, formikActions },
        onSuccess,
        onError
      )
    );
  }
};

export const addPaymentAccount = (values: Object) => async (
  dispatch: Function
) => {
  const [{ body }, err] = await to(dispatch(addPaymentAccountRequest(values)));

  if (err) {
    return Promise.reject(err);
  }

  const response = body || err.body;
  if (bodyHasError(response, { ACCOUNT_SETTINGS_ERRORS })) {
    return Promise.reject();
  }

  return Promise.resolve(body);
};

export const editPaymentAccount = (values: Object) => async (
  dispatch: Function
) => {
  const [{ body }, err] = await to(dispatch(editPaymentAccountRequest(values)));

  if (err) {
    return Promise.reject(err);
  }

  const response = body || err.body;
  if (bodyHasError(response, { ACCOUNT_SETTINGS_ERRORS })) {
    return Promise.reject();
  }

  return Promise.resolve(body);
};

export const deletePaymentAccount = (paymentAccount: PaymentAccount) => async (
  dispatch: Function
) => {
  const [{ body }, err] = await to(
    dispatch(deletePaymentAccountRequest(paymentAccount))
  );

  if (!body || err) {
    return Promise.reject(err);
  }

  const response = body || err.body;
  if (bodyHasError(response, { ACCOUNT_SETTINGS_ERRORS })) {
    return Promise.reject();
  }

  return Promise.resolve(paymentAccount);
};

export const getPaymentCardFrameUrl = () => async (
  dispatch: Function,
  getState: Function
) => {
  const state = getState();
  const countyCode = selectCountyCode(state);
  const pathname = selectPathname(state);
  const pathArr = pathname.split('/');

  const validAppSlugs = Object.keys(appSlugMap);
  const slugInPath = pathArr.find(path => validAppSlugs.includes(path));
  const agencyId = slugInPath ? appSlugMap[slugInPath] : null;
  const params = serializeParams({
    iframeCSSStyle: '{"height":"52px", "background-color":"transparent"}',
    ...(agencyId && { agencyId }),
    ...(agencyId === 'dmvrr' && { countyCode }),
  });

  try {
    const [{ body }, err] = await to(
      dispatch(getPaymentCardFrameUrlRequest(params))
    );

    if (!body || err) {
      throw err;
    }

    return Promise.resolve(body.data.url);
  } catch (err) {
    return Promise.reject(err);
  }
};
